import { useEffect, useState } from "react";
import api, { formatApiError } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../../components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";
import { Textarea } from "../../components/ui/textarea";
import { Label } from "../../components/ui/label";
import ExportButton from "../../components/ExportButton";
import PeriodFilter from "../../components/PeriodFilter";
import { useAuth } from "../../context/AuthContext";
import { openBackendFile } from "../../lib/download";
import { toast } from "sonner";
import { Check, X, Eye, ListChecks } from "lucide-react";

export default function Approvals() {
  const { user } = useAuth();
  const isAccounts = user?.role === "accounts";
  const canBulk = ["partner","admin"].includes(user?.role);
  const [timesheets, setTimesheets] = useState([]);
  const [reimbs, setReimbs] = useState([]);
  const [leaves, setLeaves] = useState([]);
  const [tsHistory, setTsHistory] = useState([]);
  const [rmbHistory, setRmbHistory] = useState([]);
  const [lvHistory, setLvHistory] = useState([]);
  const [actionDialog, setActionDialog] = useState(null);
  const [remarks, setRemarks] = useState("");
  const [selectedTs, setSelectedTs] = useState(new Set());
  const [selectedRmb, setSelectedRmb] = useState(new Set());
  const [users, setUsers] = useState([]);
  const [clients, setClients] = useState([]);
  const [userFilter, setUserFilter] = useState("");
  const [clientFilter, setClientFilter] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [period, setPeriod] = useState("");

  const dateParams = `${startDate ? `&start_date=${startDate}` : ""}${endDate ? `&end_date=${endDate}` : ""}`;
  const filterParams = `${userFilter ? `&user_id=${userFilter}` : ""}${clientFilter ? `&client_id=${clientFilter}` : ""}${dateParams}`;

  const load = () => Promise.all([
    api.get(`/timesheets?scope=pending${filterParams}`).then((r) => setTimesheets(r.data)),
    api.get(`/reimbursements?scope=pending${filterParams}`).then((r) => setReimbs(r.data)),
    api.get(`/leaves?scope=pending${userFilter ? `&user_id=${userFilter}` : ""}${dateParams}`).then((r) => setLeaves(r.data)),
    api.get(`/timesheets?scope=history${filterParams}`).then((r) => setTsHistory(r.data)),
    api.get(`/reimbursements?scope=history${filterParams}`).then((r) => setRmbHistory(r.data)),
    api.get(`/leaves?scope=all${userFilter ? `&user_id=${userFilter}` : ""}${dateParams}`).then((r) => setLvHistory((r.data || []).filter((lv) => ["approved","rejected"].includes(lv.status)))),
    api.get("/users").then((r) => setUsers(r.data)).catch(() => {}),
    api.get("/clients").then((r) => setClients(r.data)).catch(() => {}),
  ]);
  useEffect(() => { load(); }, [userFilter, clientFilter, startDate, endDate]);

  const doAction = async () => {
    if (!actionDialog) return;
    const { type, id, action } = actionDialog;
    try {
      await api.post(`/${type}/${id}/action`, { action, remarks });
      toast.success("Done");
      setActionDialog(null); setRemarks(""); load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const open = (type, id, action) => { setActionDialog({ type, id, action }); setRemarks(""); };

  const toggleTs = (id) => setSelectedTs((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const toggleRmb = (id) => setSelectedRmb((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });

  const bulkTs = async (action) => {
    if (selectedTs.size === 0) { toast.error("Select entries first"); return; }
    try { const { data } = await api.post("/timesheets/bulk-action", { ids: [...selectedTs], action, remarks: "Bulk action" }); toast.success(`${data.updated} entries ${data.status}`); setSelectedTs(new Set()); load(); }
    catch (err) { toast.error(formatApiError(err)); }
  };

  const bulkRmb = async (action) => {
    if (selectedRmb.size === 0) { toast.error("Select claims first"); return; }
    try { const { data } = await api.post("/reimbursements/bulk-action", { ids: [...selectedRmb], action, remarks: "Bulk action" }); toast.success(`${data.updated} claims ${data.status}`); setSelectedRmb(new Set()); load(); }
    catch (err) { toast.error(formatApiError(err)); }
  };

  const viewBill = async (path) => {
    try { await openBackendFile(path); } catch (e) { toast.error("Cannot open file"); }
  };

  return (
    <div className="space-y-6 fade-in" data-testid="approvals-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="eyebrow">Approvals</div>
          <h1 className="font-sans text-4xl mt-2">Decisions queue</h1>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <PeriodFilter
            value={period} start={startDate} end={endDate}
            onChange={({ period: p, start_date, end_date }) => {
              setPeriod(p);
              setStartDate(start_date || "");
              setEndDate(end_date || "");
            }}
            testId="approvals-period"
          />
          <select value={userFilter} onChange={(e)=>setUserFilter(e.target.value)} className="border border-slate-300 rounded-sm px-3 py-2 text-sm" data-testid="approvals-user-filter">
            <option value="">All users</option>
            {users.map((u)=><option key={u.id} value={u.id}>{u.name}</option>)}
          </select>
          <select value={clientFilter} onChange={(e)=>setClientFilter(e.target.value)} className="border border-slate-300 rounded-sm px-3 py-2 text-sm" data-testid="approvals-client-filter">
            <option value="">All clients</option>
            {clients.map((c)=><option key={c.id} value={c.id}>{c.client_name}</option>)}
          </select>
        </div>
      </div>

      <Tabs defaultValue="timesheets" className="w-full">
        <TabsList className="rounded-sm bg-slate-100 flex-wrap h-auto">
          {!isAccounts && <TabsTrigger value="timesheets" data-testid="tab-timesheets">Timesheets ({timesheets.length})</TabsTrigger>}
          <TabsTrigger value="reimbursements" data-testid="tab-reimbursements">Reimbursements ({reimbs.length})</TabsTrigger>
          {false && <TabsTrigger value="leaves" data-testid="tab-leaves">Leaves ({leaves.length})</TabsTrigger>}
          {!isAccounts && <TabsTrigger value="ts_history" data-testid="tab-ts-history">TS History ({tsHistory.length})</TabsTrigger>}
          <TabsTrigger value="rmb_history" data-testid="tab-rmb-history">Claim History ({rmbHistory.length})</TabsTrigger>
          {!isAccounts && <TabsTrigger value="lv_history" data-testid="tab-lv-history">Leave History ({lvHistory.length})</TabsTrigger>}
        </TabsList>

        {!isAccounts && <TabsContent value="timesheets" className="mt-4">
          <div className="flex justify-end mb-3"><ExportButton path={`/exports/timesheets?status=submitted${filterParams}`} filename="timesheets_pending.csv" testId="ts-pending-export"/></div>
          {canBulk && timesheets.length > 0 && <div className="flex items-center gap-2 mb-3">
            <span className="text-xs uppercase tracking-[0.2em] text-slate-500 mr-2">{selectedTs.size} selected</span>
            <Button onClick={()=>bulkTs("approve")} size="sm" className="bg-emerald-600 hover:bg-emerald-700 rounded-sm" data-testid="ts-bulk-approve"><ListChecks className="h-4 w-4 mr-1"/>Bulk Approve</Button>
            <Button onClick={()=>bulkTs("reject")} size="sm" variant="outline" className="rounded-sm border-red-200 text-red-700 hover:bg-red-50" data-testid="ts-bulk-reject">Bulk Reject</Button>
          </div>}
          <div className="bg-white border border-slate-200 rounded-sm overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-slate-500 uppercase tracking-[0.18em] text-xs">
                <tr>
                  {canBulk && <th className="p-4 w-8"><input type="checkbox" checked={timesheets.length > 0 && selectedTs.size === timesheets.length} onChange={(e)=>setSelectedTs(e.target.checked ? new Set(timesheets.map(t=>t.id)) : new Set())} data-testid="ts-select-all"/></th>}
                  <th className="text-left p-4">Date</th><th className="text-left p-4">User</th><th className="text-left p-4">Client</th><th className="text-left p-4">Project</th><th className="text-left p-4">Task</th><th className="text-left p-4">In/Out</th><th className="text-left p-4">Description</th><th className="text-right p-4">Hours</th><th className="p-4 text-right">Action</th></tr>
              </thead>
              <tbody className="divide-y divide-slate-100" data-testid="approvals-timesheets">
                {timesheets.length === 0 && <tr><td colSpan={canBulk ? 10 : 9} className="p-8 text-center text-slate-500">Nothing to approve.</td></tr>}
                {timesheets.map((t) => (
                  <tr key={t.id} className="hover:bg-slate-50">
                    {canBulk && <td className="p-4"><input type="checkbox" checked={selectedTs.has(t.id)} onChange={()=>toggleTs(t.id)} data-testid={`ts-select-${t.id}`}/></td>}
                    <td className="p-4 font-mono text-xs">{t.task_date}</td>
                    <td className="p-4 font-medium">{t.user_name}</td>
                    <td className="p-4 text-slate-600">{t.client_name || "—"}</td>
                    <td className="p-4 text-slate-700">{t.project_name}</td>
                    <td className="p-4 text-slate-600">{t.task_category}</td>
                    <td className="p-4 text-slate-500 text-xs font-mono whitespace-nowrap">{t.start_time || "--"} / {t.end_time || "--"}</td>
                    <td className="p-4 text-slate-700 max-w-xs">
                      <div className="line-clamp-2" title={t.description || ""}>{t.description || "—"}</div>
                      {(t.geo_lat || t.manual_location) && (
                        <div className="text-xs text-slate-400 mt-1 font-mono" title={`Location: ${t.geo_lat ? `${t.geo_lat.toFixed(4)}, ${t.geo_lng.toFixed(4)}` : t.manual_location}`}>
                          📍 {t.geo_lat ? `${Number(t.geo_lat).toFixed(4)}, ${Number(t.geo_lng).toFixed(4)}` : t.manual_location}
                        </div>
                      )}
                    </td>
                    <td className="p-4 text-right serif-num">{t.hours}</td>
                    <td className="p-4 text-right space-x-2 whitespace-nowrap">
                      <Button onClick={()=>open("timesheets", t.id, "approve")} size="sm" className="bg-emerald-600 hover:bg-emerald-700 rounded-sm" data-testid={`ts-approve-${t.id}`}><Check className="h-4 w-4"/></Button>
                      <Button onClick={()=>open("timesheets", t.id, "reject")} size="sm" variant="outline" className="rounded-sm border-red-200 text-red-700 hover:bg-red-50" data-testid={`ts-reject-${t.id}`}><X className="h-4 w-4"/></Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </TabsContent>}

        <TabsContent value="reimbursements" className="mt-4">
          <div className="flex justify-end mb-3"><ExportButton path={`/exports/reimbursements?status=submitted${filterParams}`} filename="reimbursements_pending.csv" testId="rmb-pending-export"/></div>
          {canBulk && reimbs.filter((r)=>r.status==="submitted").length > 0 && <div className="flex items-center gap-2 mb-3">
            <span className="text-xs uppercase tracking-[0.2em] text-slate-500 mr-2">{selectedRmb.size} selected</span>
            <Button onClick={()=>bulkRmb("approve")} size="sm" className="bg-emerald-600 hover:bg-emerald-700 rounded-sm" data-testid="rmb-bulk-approve"><ListChecks className="h-4 w-4 mr-1"/>Bulk Approve</Button>
            <Button onClick={()=>bulkRmb("reject")} size="sm" variant="outline" className="rounded-sm border-red-200 text-red-700 hover:bg-red-50" data-testid="rmb-bulk-reject">Bulk Reject</Button>
          </div>}
          <div className="bg-white border border-slate-200 rounded-sm overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-slate-500 uppercase tracking-[0.18em] text-xs">
                <tr>
                  {canBulk && <th className="p-4 w-8"><input type="checkbox" checked={reimbs.filter(r=>r.status==="submitted").length > 0 && selectedRmb.size === reimbs.filter(r=>r.status==="submitted").length} onChange={(e)=>setSelectedRmb(e.target.checked ? new Set(reimbs.filter(r=>r.status==="submitted").map(r=>r.id)) : new Set())} data-testid="rmb-select-all"/></th>}
                  <th className="text-left p-4">Date</th><th className="text-left p-4">User</th><th className="text-left p-4">Type</th><th className="text-left p-4">Client</th><th className="text-left p-4">Project</th><th className="text-right p-4">Amount</th><th className="text-left p-4">Description</th><th className="text-left p-4">Bill</th><th className="text-left p-4">Status</th><th className="p-4 text-right">Action</th></tr>
              </thead>
              <tbody className="divide-y divide-slate-100" data-testid="approvals-reimbursements">
                {reimbs.length === 0 && <tr><td colSpan={canBulk ? 11 : 10} className="p-8 text-center text-slate-500">Nothing to approve.</td></tr>}
                {reimbs.map((r) => (
                  <tr key={r.id} className="hover:bg-slate-50">
                    {canBulk && <td className="p-4">{r.status === "submitted" && <input type="checkbox" checked={selectedRmb.has(r.id)} onChange={()=>toggleRmb(r.id)} data-testid={`rmb-select-${r.id}`}/>}</td>}
                    <td className="p-4 font-mono text-xs">{r.claim_date}</td>
                    <td className="p-4 font-medium">{r.user_name}</td>
                    <td className="p-4 text-slate-600">{r.expense_type}</td>
                    <td className="p-4 text-slate-700">{r.client_name || "—"}</td>
                    <td className="p-4 text-slate-600">{r.project_name || "—"}</td>
                    <td className="p-4 text-right serif-num">₹{Number(r.amount).toLocaleString("en-IN")}</td>
                    <td className="p-4 text-slate-700 max-w-xs" data-testid={`rmb-desc-${r.id}`}>
                      <div className="line-clamp-2" title={r.description || ""}>{r.description || "—"}</div>
                      {(r.from_location || r.to_location || r.distance_km) && (
                        <div className="text-xs text-slate-400 mt-1">
                          {r.from_location || "—"} → {r.to_location || "—"}{r.distance_km ? ` · ${r.distance_km} km` : ""}
                        </div>
                      )}
                    </td>
                    <td className="p-4">{r.bill_path ? <button onClick={()=>viewBill(r.bill_path)} className="text-slate-500 hover:text-slate-900 inline-flex items-center gap-1 text-xs"><Eye className="h-3.5 w-3.5"/>View</button> : "—"}</td>
                    <td className="p-4 text-xs uppercase tracking-[0.2em] text-slate-500">{r.status}</td>
                    <td className="p-4 text-right space-x-2 whitespace-nowrap">
                      {r.status === "submitted" && !isAccounts && <>
                        <Button onClick={()=>open("reimbursements", r.id, "approve")} size="sm" className="bg-emerald-600 hover:bg-emerald-700 rounded-sm" data-testid={`rmb-approve-${r.id}`}><Check className="h-4 w-4"/></Button>
                        <Button onClick={()=>open("reimbursements", r.id, "reject")} size="sm" variant="outline" className="rounded-sm border-red-200 text-red-700 hover:bg-red-50" data-testid={`rmb-reject-${r.id}`}><X className="h-4 w-4"/></Button>
                      </>}
                      {r.status === "approved" && <Button onClick={()=>open("reimbursements", r.id, "paid")} size="sm" className="bg-slate-900 hover:bg-slate-800 rounded-sm" data-testid={`rmb-pay-${r.id}`}>Mark Paid</Button>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </TabsContent>

        {!isAccounts && <TabsContent value="leaves" className="mt-4">
          <div className="flex justify-end mb-3"><ExportButton path={`/exports/leaves`} filename="leaves_pending.csv" testId="lv-pending-export"/></div>
          <div className="bg-white border border-slate-200 rounded-sm overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-slate-500 uppercase tracking-[0.18em] text-xs">
                <tr><th className="text-left p-4">User</th><th className="text-left p-4">Type</th><th className="text-left p-4">From</th><th className="text-left p-4">To</th><th className="text-right p-4">Days</th><th className="text-left p-4">Reason</th><th className="p-4 text-right">Action</th></tr>
              </thead>
              <tbody className="divide-y divide-slate-100" data-testid="approvals-leaves">
                {leaves.length === 0 && <tr><td colSpan={7} className="p-8 text-center text-slate-500">Nothing to approve.</td></tr>}
                {leaves.map((lv) => (
                  <tr key={lv.id} className="hover:bg-slate-50">
                    <td className="p-4 font-medium">{lv.user_name}</td>
                    <td className="p-4 text-slate-600">{lv.leave_type}</td>
                    <td className="p-4 font-mono text-xs">{lv.start_date}</td>
                    <td className="p-4 font-mono text-xs">{lv.end_date}</td>
                    <td className="p-4 text-right serif-num">{lv.days}</td>
                    <td className="p-4 text-slate-600 max-w-xs truncate">{lv.reason}</td>
                    <td className="p-4 text-right space-x-2 whitespace-nowrap">
                      <Button onClick={()=>open("leaves", lv.id, "approve")} size="sm" className="bg-emerald-600 hover:bg-emerald-700 rounded-sm" data-testid={`lv-approve-${lv.id}`}><Check className="h-4 w-4"/></Button>
                      <Button onClick={()=>open("leaves", lv.id, "reject")} size="sm" variant="outline" className="rounded-sm border-red-200 text-red-700 hover:bg-red-50" data-testid={`lv-reject-${lv.id}`}><X className="h-4 w-4"/></Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </TabsContent>}

        {!isAccounts && <TabsContent value="ts_history" className="mt-4">
          <div className="flex justify-end mb-3"><ExportButton path="/exports/timesheets?scope=history" filename="timesheets_history.csv" testId="ts-history-export"/></div>
          <div className="bg-white border border-slate-200 rounded-sm overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-slate-500 uppercase tracking-[0.18em] text-xs">
                <tr><th className="text-left p-4">Date</th><th className="text-left p-4">User</th><th className="text-left p-4">Client/Project</th><th className="text-right p-4">Hours</th><th className="text-left p-4">Status</th><th className="text-left p-4">Remarks</th></tr>
              </thead>
              <tbody className="divide-y divide-slate-100" data-testid="ts-history-table">
                {tsHistory.length === 0 && <tr><td colSpan={6} className="p-8 text-center text-slate-500">No history.</td></tr>}
                {tsHistory.map((t) => (
                  <tr key={t.id} className="hover:bg-slate-50">
                    <td className="p-4 font-mono text-xs">{t.task_date}</td>
                    <td className="p-4">{t.user_name}</td>
                    <td className="p-4">{t.client_name || "—"} · {t.project_name}</td>
                    <td className="p-4 text-right serif-num">{t.hours}</td>
                    <td className="p-4"><span className={`text-xs uppercase tracking-[0.2em] px-2 py-1 border rounded-full ${t.approval_status === "approved" ? "border-emerald-200 text-emerald-700 bg-emerald-50" : "border-red-200 text-red-700 bg-red-50"}`}>{t.approval_status}</span></td>
                    <td className="p-4 text-slate-600 text-xs">{t.approval_remarks || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </TabsContent>}

        <TabsContent value="rmb_history" className="mt-4">
          <div className="flex justify-end mb-3"><ExportButton path="/exports/reimbursements?scope=history" filename="reimbursements_history.csv" testId="rmb-history-export"/></div>
          <div className="bg-white border border-slate-200 rounded-sm overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-slate-500 uppercase tracking-[0.18em] text-xs">
                <tr><th className="text-left p-4">Date</th><th className="text-left p-4">User</th><th className="text-left p-4">Type</th><th className="text-right p-4">Amount</th><th className="text-left p-4">Description</th><th className="text-left p-4">Status</th><th className="text-left p-4">Approver Remarks</th></tr>
              </thead>
              <tbody className="divide-y divide-slate-100" data-testid="rmb-history-table">
                {rmbHistory.length === 0 && <tr><td colSpan={7} className="p-8 text-center text-slate-500">No history.</td></tr>}
                {rmbHistory.map((r) => (
                  <tr key={r.id} className="hover:bg-slate-50">
                    <td className="p-4 font-mono text-xs">{r.claim_date}</td>
                    <td className="p-4">{r.user_name}</td>
                    <td className="p-4">{r.expense_type}</td>
                    <td className="p-4 text-right serif-num">₹{Number(r.amount).toLocaleString("en-IN")}</td>
                    <td className="p-4 text-slate-700 max-w-xs"><div className="line-clamp-2 text-xs" title={r.description || ""}>{r.description || "—"}</div></td>
                    <td className="p-4"><span className={`text-xs uppercase tracking-[0.2em] px-2 py-1 border rounded-full ${r.status === "approved" || r.status === "paid" ? "border-emerald-200 text-emerald-700 bg-emerald-50" : "border-red-200 text-red-700 bg-red-50"}`}>{r.status}</span></td>
                    <td className="p-4 text-slate-600 text-xs">{r.action_remarks || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </TabsContent>

        {!isAccounts && <TabsContent value="lv_history" className="mt-4">
          <div className="flex justify-end mb-3"><ExportButton path="/exports/leaves" filename="leaves_history.csv" testId="lv-history-export"/></div>
          <div className="bg-white border border-slate-200 rounded-sm overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-slate-500 uppercase tracking-[0.18em] text-xs">
                <tr><th className="text-left p-4">User</th><th className="text-left p-4">Type</th><th className="text-left p-4">From</th><th className="text-left p-4">To</th><th className="text-right p-4">Days</th><th className="text-left p-4">Status</th><th className="text-left p-4">Remarks</th></tr>
              </thead>
              <tbody className="divide-y divide-slate-100" data-testid="lv-history-table">
                {lvHistory.length === 0 && <tr><td colSpan={7} className="p-8 text-center text-slate-500">No history.</td></tr>}
                {lvHistory.map((lv) => (
                  <tr key={lv.id} className="hover:bg-slate-50">
                    <td className="p-4 font-medium">{lv.user_name}</td>
                    <td className="p-4 text-slate-600">{lv.leave_type}</td>
                    <td className="p-4 font-mono text-xs">{lv.start_date}</td>
                    <td className="p-4 font-mono text-xs">{lv.end_date}</td>
                    <td className="p-4 text-right serif-num">{lv.days}</td>
                    <td className="p-4"><span className={`text-xs uppercase tracking-[0.2em] px-2 py-1 border rounded-full ${lv.status === "approved" ? "border-emerald-200 text-emerald-700 bg-emerald-50" : "border-red-200 text-red-700 bg-red-50"}`}>{lv.status}</span></td>
                    <td className="p-4 text-slate-600 text-xs">{lv.action_remarks || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </TabsContent>}
      </Tabs>

      <Dialog open={!!actionDialog} onOpenChange={(v) => !v && setActionDialog(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle className="font-sans text-2xl">{actionDialog?.action === "approve" ? "Approve" : actionDialog?.action === "paid" ? "Mark Paid" : "Reject"}</DialogTitle></DialogHeader>
          <div>
            <Label className="eyebrow">Remarks (optional)</Label>
            <Textarea rows={4} value={remarks} onChange={(e)=>setRemarks(e.target.value)} className="mt-2 rounded-sm" placeholder="Add a note..." data-testid="action-remarks"/>
          </div>
          <DialogFooter>
            <Button onClick={()=>setActionDialog(null)} variant="outline" className="rounded-sm">Cancel</Button>
            <Button onClick={doAction} className={actionDialog?.action === "reject" ? "bg-red-600 hover:bg-red-700 rounded-sm" : "bg-emerald-600 hover:bg-emerald-700 rounded-sm"} data-testid="action-confirm">Confirm</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
